import { useEffect, useState, useRef } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'
import SidePanel from '../../components/common/side-panel/SidePanel'
import MainPanel from '../../components/common/main-panel/MainPanel'
import instance from '../../utils/Axios'
import { triggerGoogleAnalyticsEvent } from '../../utils/Helper'
import { useDispatch, useSelector } from 'react-redux'
import {
  currentUserSubscription,
  getAllSubscriptionProducts,
  userSubscriptionUsage,
} from '../../redux/actions/SubscriptionActions'
import FileUtils from '../../utils/FileUtils'
import LibraryAssetUploader from '../../services/library-assets/LibraryAssetUploader'

const FILE_UPLOAD_SIZE_LIMIT = 10138000

const CustomizeContent = () => {
  const {
    auth: { user },
  } = useSelector((state) => state)
  const inputRef = useRef(null)
  const [templateLogo, setTemplateLogo] = useState(user?.agreementLogoUrl || '')
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)

  const saveAgreementLogo = (logoUrl) => {
    triggerGoogleAnalyticsEvent('upload-agreement-logo', {
      userId: user?.id,
    })

    instance
      .post('/users/config', { agreementLogoUrl: logoUrl })
      .then(() => {
        toast.success('Logo uploaded successfully!')
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || 'Logo cannot be uploaded!',
        )
      })
      .finally(() => {
        setUploading(false)
      })
  }

  const selectLogo = () => {
    inputRef.current.click()
  }

  const uploadLogo = async (e) => {
    try {
      setUploading(true)
      const files = e.target.files
      if (_.isEmpty(files)) {
        return
      }

      const file = _.first(files)
      if (file.size > FILE_UPLOAD_SIZE_LIMIT) {
        return toast.error(
          'Whoa! That is a huge file size. It exceeds our max 5 GB limit. Try something smaller.',
        )
      }

      const fileName = file.name

      const fileExt = fileName.substring(fileName.lastIndexOf('.'))
      const fileNameUrl = `${moment().format('x')}${fileExt}`

      const uploadData = await LibraryAssetUploader.saveAsLibraryAsset(
        file,
        fileNameUrl,
        `assets/${FileUtils.getFileType(file.type)}s`,
        'logos',
      )
      const logoUrl = uploadData.location || uploadData.Location
      saveAgreementLogo(logoUrl)
      setTemplateLogo(logoUrl)
      sessionStorage.setItem('agreementLogoUrl', logoUrl)
    } catch (error) {
      setUploading(false)
      toast.error('Unable to upload logo. Please try again.')
    }
  }

  useEffect(() => {
    const sessionStoredAgreementLogoUrl =
      sessionStorage.getItem('agreementLogoUrl')
    if (sessionStoredAgreementLogoUrl) {
      setTemplateLogo(sessionStoredAgreementLogoUrl)
    }
    setLoading(false)
  }, [])

  return (
    <MainPanel>
      <div className="customize-container">
        <div className="title">
          <h2>Customize</h2>
        </div>
        <div className="customize-container__section">
          {!loading && (
            <div className="agreement-logo active">
              <h4>
                {templateLogo ? 'Change Agreement Logo' : 'Add Agreement Logo'}
              </h4>
              <p>
                Customise agreements with your logo. JPG, GIF, and PNG images
                are supported. Images should be 60 pixels tall and up to 200
                pixels wide.
              </p>
              {templateLogo && (
                <div className="logo-upload-preview">
                  <img src={templateLogo} alt="template-logo" />
                </div>
              )}

              <button disabled={uploading} onClick={selectLogo}>
                {uploading
                  ? 'Uploading...'
                  : templateLogo
                  ? 'Change'
                  : 'Upload'}
              </button>
              <input
                ref={inputRef}
                type="file"
                id="upload"
                style={{ display: 'none' }}
                onChange={uploadLogo}
                accept={'image/*'}
              />
            </div>
          )}
        </div>
      </div>
    </MainPanel>
  )
}

const Customize = () => {
  const dispatch = useDispatch()

  const getSubscriptionUsage = async () => {
    await dispatch(userSubscriptionUsage())
  }

  const loadUserSubscription = async () => {
    await dispatch(currentUserSubscription())
  }

  const loadAllSubscriptionProducts = async () => {
    await dispatch(getAllSubscriptionProducts())
  }

  useEffect(() => {
    getSubscriptionUsage()
    loadUserSubscription()
    loadAllSubscriptionProducts()
  }, [])

  return (
    <div className="tikbox-customize">
      <div className="tikbox-customize__content">
        <SidePanel />
        <CustomizeContent />
      </div>
    </div>
  )
}

export default Customize
